<template>
  <div class="contacts">

    <div class="page__header">
      <div class="page__title">{{ $t(`pageTitles.${"contact"}`) }}</div>

      <div class="contacts__buttons">
        <button type="button" class="button button--default button--shadow"
          @click="$store.commit('setShowAddCompanyContactModal', true)">
          {{ $t(`contact.${"addNewContact"}`) }}
        </button>
      </div>
    </div>

    <div class="contacts__content">
      <div class="contacts__list box box--default" :class="{ 'contacts__list--hide': showModels }">
        <div class="label label--gradient">{{ $t(`contact.${"companies"}`) }}</div>

        <Loading v-if="companiesLoading" />
        <template v-else>
          <div class="companies">

            <VSelect :options="loggedUser.teams" :reduce="(team) => team.id" placeholder="Team" label="name"
              v-model="userTeam" class="select select--default select-column" style="margin-bottom: 15px;"
              @update:modelValue="initializeTeamData($event)" />

            <div class="companies__search" v-if="userTeam !== null">
              <form class="search" @submit.prevent>
                <input type="text" class="search__search" :class="{ 'search--active': companySearchKeywords }"
                  v-model="companySearchKeywords" />
              </form>
            </div>

            <div class="companies__list" v-if="userTeam !== null">
              <Contact v-for="company in filteredCompanies" :key="company.id" :company="company"
                :selectedCompany="selectedCompany" :selectCompany="selectCompany" />
            </div>
          </div>
        </template>
      </div>

      <div class="contacts__overview box box--default" :class="{ 'contacts__overview--toggled': showModels }">
        <div class="label label--gradient">{{ $t(`contact.${"contact"}`) }}</div>
        <Loading v-if="companiesLoading || selectingCompany || applyingServices || isUpdatingCompany" />
        <template v-else>
          <div class="contact" v-if="userTeam !== null">
            <div class="company">
              <div class="company__header">
                <div class="company__logo" v-if="userTeam != null">
                  <img v-if="selectedCompany.logo" :src="selectedCompany.logo.url" alt="" />
                </div>
                <span v-if="userTeam != null">{{ selectedCompany.name }}</span>
                <div v-if="userTeam != null" class="company__edit icon icon__edit" @click="editContact()"></div>
                <div v-if="userTeam != null && $checkRolePermission('team_delete')"
                  class="company__delete icon icon__delete" @click="deleteCompanyModal(selectedCompany.id)"></div>
              </div>

              <div class="company__contacts" v-if="selectedCompany && userTeam != null">
                <div class="company__contact" v-if="selectedCompany.address || selectedCompany.city || selectedCompany.zip">
                  <div class="company__icon company__icon--location"></div>
                  <div class="company__detail" v-if="userTeam != null">
                    {{ selectedCompany.address }}
                  </div>
                  <div class="company__detail" v-if="selectedCompany.city">{{ selectedCompany.city }}</div>
                  <div class="company__detail" v-if="selectedCompany.zip">{{ selectedCompany.zip }}</div>
                </div>

                <div class="company__contact" v-if="selectedCompany.phone">
                  <div class="company__icon company__icon--mobile"></div>
                  <div class="company__detail">{{ selectedCompany.phone }}</div>
                </div>

                <div class="company__contact" v-if="selectedCompany.email || selectedCompany.mandate_email">
                  <div class="company__icon company__icon--mail"></div>
                  <div class="company__detail" v-if="selectedCompany.email">{{ selectedCompany.email }}</div>
                  <div class="company__detail" v-if="selectedCompany.mandate_email">{{ selectedCompany.mandate_email }}
                  </div>
                </div>

                <div class="company__contact" v-if="selectedCompany.web">
                  <div class="company__icon company__icon--web"></div>
                  <div class="company__detail">{{ selectedCompany.web }}</div>
                </div>

                <div class="company__contact">
                  <div v-if="selectedCompany.mandate_distribution_list" class="company__icon icon icon__active"></div>
                  <div v-else class="company__icon icon icon__inactive"></div>
                  <div class="company__detail">{{$t(`addCompanyContactModal.${"mandateList"}`) }}</div>
                </div>
              </div>
            </div>

            <div class="company__contact comment" v-if="selectedCompany.comment">
              <div class="company__detail">{{ selectedCompany.comment }}</div>
            </div>

            <div class="contact__map-wrapper">
              <div class="contact__services" :class="{
                'contact__services--center': !selectedCompany.services.length,
              }">
                <div class="services">
                  <div class="service" v-for="service in selectedCompany.services" :key="service.id"
                    @click="selectService(service)">
                    {{ service[`long_${language}`] }}
                  </div>
                  <DropdownServices class="service--add" :dropdownServices="branchCategoryServices"
                    :services="selectedCompany.services" :applyServices="applyServices"
                    :addOrRemoveServices="addOrRemoveServices" :closeServicesDropdown="closeServicesDropdown" />
                </div>
              </div>
              <div class="contact__map">
                <Map :lan="selectedCompany.lan" :lat="selectedCompany.lat" :address="companyAddress" />
              </div>
            </div>
          </div>
        </template>
      </div>

      <transition name="fade">
        <div class="contacts__models box box--default" :class="{ 'contacts__models--show': showModels }"
          v-if="showModels">
          <div class="label label--gradient">
            <span>
              {{ selectedService.name }}
              <div class="label__close" @click="showModels = false"></div>
            </span>
          </div>

          <Loading v-if="modelsLoading" />
          <template v-else>
            <div class="models">
              <NoData v-if="!models.length" :type="'models'" />
              <div class="models__list">
                <template v-if="models.length">
                  <Model v-for="model in models" :key="model.id" :model="model" :getModels="getModels" />
                </template>
              </div>
              <button class="button button--default button--shadow" v-if="models.length"
                @click="$store.commit('setShowAddModelModal', true)">
                {{ $t(`contact.${"addNewModelButton"}`) }}
              </button>
            </div>
          </template>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import Contact from "@/components/Contacts/Contact";
import Loading from "@/partials/Loading";
import Map from "@/components/Contacts/Map";
import Model from "@/components/Contacts/Model";
import NoData from "@/partials/NoData";
import DropdownServices from "@/partials/DropdownServices";

const store = useStore();
const router = useRouter();
const instance = getCurrentInstance();

// Data
const companySearchKeywords = ref("");
const showModels = ref(false);
const selectedService = ref({
  name: "",
  id: "",
});
const modelsLoading = ref(false);
const preServices = ref([]);
const userTeam = ref(null);

const selectingCompany = ref(false);
const applyingServices = ref(false);

// Computed
const companies = computed(() => store.getters.companies);
const models = computed(() => store.getters.models);
const branchCategoryServices = computed(() => store.getters.branchCategoryServices);
const selectedCompany = computed(() => store.getters.selectedCompany);
const language = computed(() => store.getters.language);
const companiesLoading = computed(() => store.getters.companiesLoading);
const loggedUser = computed(() => store.getters.loggedUser);
const isUpdatingCompany = computed(() => store.getters.isUpdatingCompany);

const filteredCompanies = computed(() => {
  if (!companies.value || !Array.isArray(companies.value)) return [];

  const search = companySearchKeywords.value?.toLowerCase().trim() || "";

  return companies.value.filter((company) => {
    if (!company || !company.name) return false;

    return company.name
      .toLowerCase()
      .trim()
      .includes(search);
  });
});

const companyAddress = computed(() => {
  if (!selectedCompany.value) return "";

  const city = selectedCompany.value.city || "";
  const zip = selectedCompany.value.zip || "";
  const address = selectedCompany.value.address || "";

  return `${city} ${zip} ${address}`.trim();
});

// Watch
watch(
  () => selectedCompany.value.id,
  async (companyId) => {
    if (!selectedService.value.id) return;
    modelsLoading.value = true;
    await getModels(selectedService.value.id, companyId);
    modelsLoading.value = false;
  }
);

// Methods
const initializeTeamData = async (teamId) => {
  try {
    store.commit("setCompaniesLoading", true);
    await store.dispatch("getBranchCategoryServices");
    await store.dispatch("getCompanies", teamId);
    const [firstCompany] = companies.value;
    if (!firstCompany) {
      console.warn("No companies found for team:", teamId);
      store.commit("setCompaniesLoading", false);
      return;
    }
    store.commit("setSelectedCompany", firstCompany);
    const [firstService] = firstCompany.services || [];
    if (firstService) {
      selectedService.value.id = firstService.id;
      selectedService.value.name = firstService[`long_${language.value}`];
      store.commit("setSelectedService", firstService);
      await getModels(firstService.id, firstCompany.id);
    }
    preServices.value = [...selectedCompany.value.services];
  } catch (error) {
    console.error("Error initializing team data:", error);
  } finally {
    store.commit("setCompaniesLoading", false);
    userTeam.value = teamId;
  }
};

// const selectCompany = (company) => {
//   store.commit("setSelectedCompany", company);
// };

const selectCompany = async (company) => {
  try {
    selectingCompany.value = true;
    const freshCompanyData = await store.dispatch("getCompany", company.id);
    store.commit("setSelectedCompany", freshCompanyData);
    preServices.value = [...freshCompanyData.services];
  } catch (err) {
    console.error("Error fetching company details:", err);
  } finally {
    selectingCompany.value = false;
  }
};

const selectService = (service) => {
  selectedService.value.id = service.id;
  selectedService.value.name = service[`long_${language.value}`];
  store.commit("setSelectedService", service);
  showModels.value = true;
};

const addOrRemoveServices = (event, service) => {
  const { checked } = event.target;
  const existingService = preServices.value.findIndex(
    (s) => s.id === service.id
  );
  if (checked) {
    if (existingService === -1) {
      preServices.value = [...preServices.value, service];
    }
  } else {
    preServices.value.splice(existingService, 1);
  }
};

const applyServices = async () => {
  const services = preServices.value.map((service) => service.id);
  const previousServices = [...selectedCompany.value.services];
  try {
    applyingServices.value = true;
    await store.dispatch("updateCompany", {
      companyId: selectedCompany.value.id,
      services,
    });
    const company = await store.dispatch(
      "getCompany",
      selectedCompany.value.id
    );

    const indexOfCompanyToReplace = companies.value.findIndex(
      (c) => c.id === company.id
    );
    const tempArray = [...companies.value];
    tempArray[indexOfCompanyToReplace] = company;
    store.commit("setSelectedCompany", company);
    store.commit("setCompanies", tempArray);
    store.commit("setToast", {
      display: true,
      type: "success",
      message: "Services updated successfully",
    });
  } catch (err) {
    console.error(err);
    let displayMessage = "Failed to update services";

    if (err.response && err.response.data) {
      const errorData = err.response.data;
      if (errorData.message) {
        displayMessage = errorData.message;
      } else if (errorData.error && typeof errorData.error === 'string') {
        displayMessage = errorData.error;
      }
    }

    store.commit("setToast", {
      display: true,
      type: "error",
      message: displayMessage,
    });
    preServices.value = [...previousServices];
  } finally {
    applyingServices.value = false;
  }
};

const closeServicesDropdown = () => {
  preServices.value = [...selectedCompany.value.services];
};

const getModels = async (
  serviceId = selectedService.value.id,
  companyId = selectedCompany.value.id
) => {
  try {
    const models = await store.dispatch(
      "getModelsByCompanyAndServices",
      {
        companyId,
        serviceId,
      }
    );
    store.commit("setModels", models);
  } catch (err) {
    console.error(err);
  }
};

const editContact = () => {
  store.commit("setContactToEdit", selectedCompany.value);
  store.commit("setContactToEditTeam", userTeam.value);
  store.commit("setShowAddCompanyContactModal", true);
};

const deleteCompanyModal = (id) => {
  store.commit("setDeleteModal", {
    display: true,
    message: '',
    action: () => deleteSelectedCompany(id, userTeam.value),
    id: id,
  });
};

const deleteSelectedCompany = async (companyId, teamId) => {
  await store.dispatch('deleteCompany', companyId);
  await initializeTeamData(teamId);
};

// Created/Mounted logic
(async () => {
  const checkRolePermission = instance.appContext.config.globalProperties.$checkRolePermission;

  if (!checkRolePermission('company_access')) {
    router.push('/dashboard');
  } else {
    if (loggedUser.value.teams.length == 1) {
      store.commit("setCompaniesLoading", true);

      await store.dispatch("getBranchCategoryServices");
      await store.dispatch("getCompanies", loggedUser.value.teams[0].id);
      const [firstCompany] = companies.value;
      store.commit("setSelectedCompany", firstCompany);
      const [firstService] = firstCompany.services;
      selectedService.value.id = firstService.id;
      selectedService.value.name = firstService[`long_${language.value}`];
      store.commit("setSelectedService", firstService);
      preServices.value = [...selectedCompany.value.services];
      await getModels(firstService.id, firstCompany.id);

      userTeam.value = loggedUser.value.teams[0].id;

      store.commit("setCompaniesLoading", false);
    } else {
      store.commit("setCompaniesLoading", false);
    }
  }
})();
</script>

<style scoped lang="scss">
.contacts {
  height: 100%;

  &__content {
    display: flex;
    padding-top: 30px;
    height: calc(100% - 48px - 20px - 10px);
    position: relative;
    overflow-x: hidden;
  }

  &__buttons {
    display: flex;

    .button {
      width: 190px;

      &:first-of-type {
        margin-right: 50px;
      }
    }
  }

  &__list,
  &__overview,
  &__models {
    background: var(--bg-1);
    position: relative;
    height: 100%;
    z-index: 2;
    transition: all 0.3s ease;
  }

  &__list,
  &__models {
    width: 500px;
    position: absolute;
    height: calc(100% - 30px);
  }

  &__models {
    z-index: 0;
    right: 0;
    padding-top: 50px;

    &--show {
      margin-left: 60px;
      z-index: 4;
    }

    span {
      position: relative;

      .label__close {
        position: absolute;
        top: -25px;
        right: -35px;
        width: 20px;
        height: 20px;
        background: url("../assets/icons/default/x_delete_icon.svg") no-repeat center;
        background-size: 100%;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          transform: rotate(180deg);
        }
      }
    }

    .models {
      height: 100%;

      &__list {
        overflow: auto;
        margin-bottom: 15px;
        max-height: calc(100% - 48px - 15px - 15px);
      }

      .button {
        margin: auto;
      }
    }
  }

  &__list {
    margin-right: 60px;
    transform: translateX(0);
    opacity: 1;

    &--hide {
      transform: translateX(-100%);
      opacity: 0;
      margin-right: 0;
    }

    .companies {
      padding: 60px 10px 10px 10px;
      height: 100%;

      &__search {
        margin-bottom: 20px;

        .search {
          &__search {
            padding-left: 45px;
            width: 100%;
            background-position: 10px center;
          }
        }
      }

      &__list {
        padding-top: 10px;
        max-height: calc(100% - 40px - 20px);
        overflow: auto;
        display: flex;
        flex-wrap: wrap;
      }
    }
  }

  &__overview {
    width: calc(100% - 500px - 60px);
    margin-left: 560px;

    &--toggled {
      margin-left: 0;
    }

    .contact {
      padding-top: 80px;
      height: 100%;
      display: flex;
      flex-direction: column;

      .company {
        flex: 1;

        &__header {
          background: var(--bg-17);
          padding: 12px 0;
          color: var(--text-9);
          font-size: var(--medium);
          text-align: center;
          position: relative;
        }

        &__logo {
          position: absolute;
          height: 81px;
          width: 165px;
          background: var(--bg-1);
          border-radius: var(--radius-1);
          box-shadow: 0px 3px 7px var(--shadow-1);
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;

          img {
            max-height: 54px;
          }
        }

        &__edit,
        &__delete {
          position: absolute;
          right: 50px;
          top: 50%;
          transform: translateY(-50%);
        }

        &__delete {
          right: 20px;
        }

        &__contacts {
          // margin: 60px 0 0 80px;
          margin: 60px 0 0 15px;
          display: flex;
        }

        &__contact {
          flex: 1;
        }

        &__detail {
          margin-bottom: 8px;
          color: var(--text-9);
        }

        &__icon {
          height: 24px;
          width: 24px;
          margin-bottom: 20px;

          &--location {
            background: url("../assets/icons/default/location_icon.svg");
          }

          &--mobile {
            background: url("../assets/icons/default/mobile_icon.svg");
          }

          &--mail {
            background: url("../assets/icons/default/mail_icon.svg");
          }

          &--web {
            background: url("../assets/icons/default/web_icon.svg");
          }
        }
      }

      &__services {
        padding: 5px 15px;

        &--center {
          .services {
            justify-content: center;
          }
        }

        .services {
          overflow: auto;
          max-height: 184px;
          padding: 20px 10px 2px 10px;
          background: var(--bg-17);
          border-radius: var(--radius-1);
          display: flex;
          align-items: center;
          flex-wrap: wrap;

          .service {
            background: var(--bg-2);
            cursor: pointer;
            padding: 10px 5px;
            color: var(--text-1);
            margin: 0 18px 18px 0;
            border-radius: var(--radius-1);
            box-shadow: 0px 7px 18px var(--shadow-10);
            transition: all 0.3s ease;

            &:hover:not(.service--add) {
              background: var(--hover-1);
              box-shadow: 0px 0px 20px var(--shadow-10);
            }

            &--add {
              box-shadow: none;
              margin: 0 18px 18px 0;
            }
          }
        }
      }

      &__map-wrapper {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
      }

      &__map {
        padding: 0 15px 15px 15px;
        width: 100%;
        height: 100%;
        flex: 1;
      }
    }
  }

  .comment {
    margin: 20px 0;
    padding: 0 20px;
  }
}
</style>
